import { MoreHorizontal } from "lucide-react";
import { useState } from "react";
import type { Department, Project, User, Session, Task, TenantSettings } from "../api";

export function canManageTask(task: Task, session: Pick<Session, "userId" | "roles">, user: User | undefined, project: Project | undefined, departments: Department[]) {
  const managesDepartment = session.roles.includes("MANAGER") && Boolean(user && userManagesWithin(user, task.departmentId, departments));
  // 일감 업무는 담당 팀에서, 일감 없이 프로젝트에 속한 업무는 PM이 관리한다.
  return session.roles.includes("OWNER") || (task.workItemId ? managesDepartment : project
    ? project.members.some(member => member.userId === session.userId && member.role === "PM") : managesDepartment);
}

/** Pending decisions, independent of notification read state and personal board filters. */
export function taskNeedsApproval(task: Task, user: User | undefined, project: Project | undefined, departments: Department[]): boolean {
  if (!user || user.status !== "ACTIVE") return false;
  if (task.status === "APPROVAL_PENDING") return Boolean(task.collaborationSourceDepartmentId
    && task.collaborationApproverId === user.id && userManagesDepartment(user, task.collaborationSourceDepartmentId));
  if (task.status === "REQUESTED") {
    const manager = user.roles.includes("OWNER") || userManagesDepartment(user, task.departmentId);
    return manager && (Boolean(task.collaborationSourceDepartmentId) || (!task.assigneeIds.includes(user.id)
      && (!task.approvalManagerId || task.approvalManagerId === user.id)));
  }
  if (task.status === "IN_PROGRESS" && task.scheduleChangeRequest?.status === "PENDING") return Boolean(task.scheduleChangeActionRequired);
  return ["IN_REVIEW", "COMPLETION_REVIEW"].includes(task.status) && !task.assigneeIds.includes(user.id)
    && canManageTask(task, { userId: user.id, roles: user.roles }, user, project, departments);
}

export function canEditTaskInstruction(task: Task, session: Pick<Session, "userId" | "roles">, user: User | undefined) {
  const manager = session.roles.includes("OWNER") || Boolean(user && userManagesDepartment(user, task.departmentId));
  if (task.status === "REQUESTED") return !task.collaborationSourceDepartmentId && manager;
  if (task.status === "DRAFT") return task.proposerId && task.proposerId !== task.issuerId ? manager : task.issuerId === session.userId;
  return task.status === "REJECTED" && Boolean(task.collaborationSourceDepartmentId) && task.issuerId === session.userId;
}

export function canConvertTaskToNote(task: Task, session: Session, user: User | undefined, departments: Department[], role: TenantSettings["researchNoteConversionRole"]) {
  if (task.status !== "COMPLETED") return false;
  const manager = session.roles.includes("OWNER") || (session.roles.includes("MANAGER") && Boolean(user && userDepartmentMemberships(user).some((membership) =>
    membership.role === "MANAGER" && departmentWithin(membership.departmentId, task.departmentId, departments))));
  return manager || (role === "ASSIGNEE" && (task.issuerId === session.userId || task.assigneeIds.includes(session.userId)));
}

export function departmentWithin(rootId: string | null | undefined, targetId: string | null | undefined, departments: Department[]) {
  if (!rootId || !targetId) return false;
  let cursor: string | undefined = targetId;
  const seen = new Set<string>();
  while (cursor && !seen.has(cursor)) {
    if (cursor === rootId) return true;
    seen.add(cursor);
    cursor = departments.find((department) => department.id === cursor)?.parentId || undefined;
  }
  return false;
}

export function orderedDepartments(departments: Department[], excluded = new Set<string>()) {
  const visible = departments.filter((department) => !excluded.has(department.id));
  const byId = new Map(visible.map((department) => [department.id, department]));
  const children = new Map<string | null, Department[]>();
  visible.forEach((department) => {
    const parent = department.parentId && byId.has(department.parentId) ? department.parentId : null;
    children.set(parent, [...(children.get(parent) || []), department]);
  });
  children.forEach((items) => items.sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name, "ko")));
  const result: { department: Department; depth: number }[] = [];
  const visit = (parent: string | null, depth: number) => (children.get(parent) || []).forEach((department) => {
    result.push({ department, depth }); visit(department.id, depth + 1);
  });
  visit(null, 0);
  return result;
}

export function departmentDescendants(id: string, departments: Department[]): Set<string> {
  const result = new Set<string>([id]);
  let changed = true;
  while (changed) {
    changed = false;
    departments.forEach((department) => {
      if (department.parentId && result.has(department.parentId) && !result.has(department.id)) { result.add(department.id); changed = true; }
    });
  }
  return result;
}

export function userDepartmentMemberships(user: User): User["departmentMemberships"] {
  return user.departmentMemberships ?? (user.departmentId ? [{
    departmentId: user.departmentId,
    role: user.roles.includes("MANAGER") ? "MANAGER" : "MEMBER",
    primary: true,
  }] : []);
}

export function userDepartmentIds(user: User): string[] {
  return userDepartmentMemberships(user).map((membership) => membership.departmentId);
}

export function userBelongsTo(user: User, departmentId: string | null | undefined): boolean {
  return Boolean(departmentId && userDepartmentIds(user).includes(departmentId));
}

export function userWithinDepartment(user: User, rootId: string | null | undefined, departments: Department[]): boolean {
  return userDepartmentIds(user).some((departmentId) => departmentWithin(rootId, departmentId, departments));
}

export function userManagesDepartment(user: User, departmentId: string | null | undefined): boolean {
  if (!departmentId) return false;
  return userDepartmentMemberships(user).some((membership) =>
    membership.departmentId === departmentId && membership.role === "MANAGER");
}

export function userManagedDepartmentIds(user: User): string[] {
  const managed = userDepartmentMemberships(user).filter((membership) => membership.role === "MANAGER")
    .map((membership) => membership.departmentId);
  return managed.length ? managed : (user.roles.includes("MANAGER") && user.departmentId ? [user.departmentId] : []);
}

export function userManagesWithin(user: User, targetId: string | null | undefined, departments: Department[]): boolean {
  return userManagedDepartmentIds(user).some((managedId) => departmentWithin(managedId, targetId, departments));
}

/** 서버의 실질 상태(status)와 관리자가 정한 사용 여부를 구분한다. */
export function departmentConfiguredActive(department: Department): boolean {
  return (department.configuredStatus ?? department.status) === "ACTIVE";
}

export function departmentNeedsLeader(department: Department): boolean {
  return departmentConfiguredActive(department) && !(department.hasActiveLeader ?? department.status === "ACTIVE");
}

/**
 * 드롭다운의 팀 목록. 상위 팀 이름을 앞에 붙여 어느 갈래인지 한 줄로 알 수 있게 한다.
 * 들여쓰기만으로는 목록이 열렸을 때 갈래가 잘 보이지 않았다.
 */
export function DepartmentOptions({ departments, excluded, includeInactive = false }: { departments: Department[]; excluded?: Set<string>; includeInactive?: boolean }) {
  const visible = includeInactive ? departments : departments.filter((department) => department.status === "ACTIVE");
  const byId = new Map(visible.map((department) => [department.id, department]));
  const path = (department: Department): string[] => {
    const parent = department.parentId ? byId.get(department.parentId) : undefined;
    return parent ? [...path(parent), department.name] : [department.name];
  };
  return <>{orderedDepartments(visible, excluded).map(({ department, depth }) => <option value={department.id} key={department.id}>
    {`${"　".repeat(depth)}${depth ? "└ " : ""}${department.name}`}
    {depth > 0 ? `   (${path(department).slice(0, -1).join(" / ")})` : ""}
    {!departmentConfiguredActive(department) ? " · 비활성" : departmentNeedsLeader(department) ? " · 설정 필요" : ""}
  </option>)}</>;
}

/**
 * 팀 목록 — 전체 / 트리 / 미분류 순서로 한 줄씩. 행을 누르면 그 팀의 구성원만 보여 준다.
 * 관리 작업은 팀 이름 옆의 메뉴에서 이동과 삭제로 명확히 나눈다.
 */
export function DepartmentTree({ departments, users, admin, selectedId, onSelect, onAddMember, onAssignLeader, onMove, onDelete }: {
  departments: Department[]; users: User[]; admin: boolean;
  selectedId: string | null; onSelect: (id: string | null) => void;
  onAddMember: (department: Department) => void; onAssignLeader: (department: Department) => void;
  onMove: (department: Department) => void; onDelete: (department: Department) => void;
}) {
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const ordered = orderedDepartments(departments);
  const known = new Set(departments.map((department) => department.id));
  const unassigned = users.filter((user) => user.status !== "DEPARTED"
    && !userDepartmentIds(user).some((id) => known.has(id))).length;
  const descendantCount = (id: string) => {
    const ids = new Set([id]);
    let grew = true;
    while (grew) {
      grew = false;
      departments.forEach((department) => {
        if (department.parentId && ids.has(department.parentId) && !ids.has(department.id)) { ids.add(department.id); grew = true; }
      });
    }
    return users.filter((user) => userDepartmentIds(user).some((departmentId) => ids.has(departmentId))).length;
  };

  return <div className="dept-rows">
    <div className={`dept-row${selectedId === null ? " on" : ""}`} style={{ paddingLeft: "10px" }}>
      <button type="button" onClick={() => onSelect(null)}>전체</button>
      <span className="mono">{users.length}</span>
    </div>
    {ordered.map(({ department, depth }) => {
      const configuredInactive = !departmentConfiguredActive(department);
      const setupRequired = departmentNeedsLeader(department);
      const hasActiveLeader = department.hasActiveLeader ?? users.some((user) => user.status === "ACTIVE" && userBelongsTo(user, department.id)
        && (userManagesDepartment(user, department.id) || user.roles.includes("OWNER")));
      return <div className={`dept-row${configuredInactive ? " off" : ""}${setupRequired ? " setup" : ""}${selectedId === department.id ? " on" : ""}`} key={department.id}
        style={{ paddingLeft: `${10 + depth * 14}px` }}>
        <button type="button" onClick={() => onSelect(department.id)}>
          {department.name}{configuredInactive
            ? <em>{hasActiveLeader ? "비활성" : "비활성 · 매니저 없음"}</em>
            : setupRequired && <em>설정 필요 · 매니저 없음</em>}
        </button>
        {admin && <span className="row-menu dept-actions">
          <button type="button" aria-label={`${department.name} 메뉴`} aria-expanded={openMenu === department.id}
            onClick={() => setOpenMenu(openMenu === department.id ? null : department.id)}><MoreHorizontal size={14} /></button>
          {openMenu === department.id && <span className="row-menu-list">
            <button type="button" onClick={() => { setOpenMenu(null); onAddMember(department); }}>구성원 추가</button>
            <button type="button" onClick={() => { setOpenMenu(null); onAssignLeader(department); }}>{hasActiveLeader ? "기본 관리자 지정" : "매니저 지정"}</button>
            <button type="button" onClick={() => { setOpenMenu(null); onMove(department); }}>이동</button>
            <button type="button" className="menu-danger" onClick={() => { setOpenMenu(null); onDelete(department); }}>삭제</button>
          </span>}
        </span>}
        <span className="mono">{descendantCount(department.id)}</span>
      </div>;
    })}
    <div className={`dept-row${selectedId === UNASSIGNED ? " on" : ""}`} style={{ paddingLeft: "10px" }}>
      <button type="button" onClick={() => onSelect(UNASSIGNED)}>미분류</button>
      <span className="mono">{unassigned}</span>
    </div>
  </div>;
}

/** 팀이 없는 구성원을 고르는 표식. 실제 팀 id와 섞이지 않게 따로 둔다. */
export const UNASSIGNED = "__unassigned__";

export function primaryRole(user: User): User["roles"][number] {
  if (user.roles.includes("OWNER")) return "OWNER";
  if (user.roles.includes("MANAGER")) return "MANAGER";
  return "MEMBER";
}
